import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultibancoConfig } from '../../../core/models/payment.model';
import { PriceService } from '../../../core/services/price.service';

/**
 * Presentational component that renders the Multibanco coordinates
 * returned by GET /api/payments/mb/orders/:orderId/config (and as
 * gatewayData from processPayment).
 *
 * Used in two places:
 *  - The payment-processing dialog at checkout (full-size, with the
 *    prominent Copy Reference CTA wrapping it).
 *  - The order-details page reached from the order confirmation email
 *    (compact mode, embedded in the payment-box).
 *
 * Every value has an inline copy icon — useful on both surfaces since
 * the customer is typing these into an ATM or homebanking form.
 */
@Component({
  selector: 'app-multibanco-details',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    <div class="info-rows" [class.compact]="compact()">
      <div class="info-row">
        <span class="label">{{ 'PAYMENT_PROCESSING.MULTIBANCO.ENTITY' | translate }}</span>
        <span class="value monospace">{{ config().entity }}</span>
        <button type="button" class="copy-icon"
                [matTooltip]="(copiedKey() === 'entity' ? 'PAYMENT_PROCESSING.MULTIBANCO.COPIED' : 'COMMON.COPY') | translate"
                [attr.aria-label]="ariaLabelFor('ENTITY', 'entity')"
                (click)="copy('entity', config().entity)">
          <mat-icon>{{ copiedKey() === 'entity' ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </div>
      <div class="info-row">
        <span class="label">{{ 'PAYMENT_PROCESSING.MULTIBANCO.REFERENCE' | translate }}</span>
        <span class="value monospace reference">{{ config().reference }}</span>
        <button type="button" class="copy-icon"
                [matTooltip]="(copiedKey() === 'reference' ? 'PAYMENT_PROCESSING.MULTIBANCO.COPIED' : 'COMMON.COPY') | translate"
                [attr.aria-label]="ariaLabelFor('REFERENCE', 'reference')"
                (click)="copy('reference', config().reference)">
          <mat-icon>{{ copiedKey() === 'reference' ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </div>
      <div class="info-row">
        <span class="label">{{ 'PAYMENT_PROCESSING.MULTIBANCO.AMOUNT' | translate }}</span>
        <span class="value amount">{{ priceService.formatForCurrencyId(amountNumber(), currencyId()) }}</span>
        <button type="button" class="copy-icon"
                [matTooltip]="(copiedKey() === 'amount' ? 'PAYMENT_PROCESSING.MULTIBANCO.COPIED' : 'COMMON.COPY') | translate"
                [attr.aria-label]="ariaLabelFor('AMOUNT', 'amount')"
                (click)="copy('amount', amountNumber().toFixed(2))">
          <mat-icon>{{ copiedKey() === 'amount' ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .info-rows {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
    }
    .info-rows.compact {
      background: transparent;
      padding: 0;
      .info-row {
        padding: 4px 0;
        gap: 6px;
        &:not(:last-child) { border-bottom: 1px dashed #e0e0e0; }
        .label { font-size: 11px; }
        .value { font-size: 12px; }
        .monospace { font-size: 11px; letter-spacing: 0; }
        .amount { font-size: 12px; }
      }
      .copy-icon {
        padding: 2px;
        mat-icon {
          font-size: 14px;
          width: 14px;
          height: 14px;
        }
      }
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      gap: 12px;
      &:not(:last-child) { border-bottom: 1px solid #e0e0e0; }
      .label { color: #666; font-size: 14px; flex-shrink: 0; }
      .value { font-weight: 500; font-size: 15px; text-align: right; word-break: break-all; flex: 1; }
      .monospace { font-family: monospace; letter-spacing: 0.5px; }
      .reference { letter-spacing: 1px; }
      .amount { color: #2e7d32; font-size: 16px; }
    }
    .copy-icon {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #666;
      flex-shrink: 0;
      &:hover { background: rgba(0, 0, 0, 0.06); color: #2e5339; }
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }
  `]
})
export class MultibancoDetailsComponent {
  private translate = inject(TranslateService);
  protected priceService = inject(PriceService);

  config = input.required<MultibancoConfig>();
  // When true, render compressed for embedding inside the order-details
  // payment-box. Same trade-off as BankTransferDetailsComponent's compact mode.
  compact = input<boolean>(false);
  // Optional currencyId of the order. When supplied, the amount is
  // formatted against THAT currency (not the viewer's session currency).
  currencyId = input<number | null | undefined>(undefined);

  // Backend amount is a 6-decimal string ("43.720000"). Parse once per change.
  amountNumber = computed(() => parseFloat(this.config().amount) || 0);

  copiedKey = signal<string | null>(null);

  ariaLabelFor(labelKey: string, fieldKey: string): string {
    const fieldLabel = this.translate.instant(`PAYMENT_PROCESSING.MULTIBANCO.${labelKey}`);
    const copyLabel = this.translate.instant(
      this.copiedKey() === fieldKey ? 'PAYMENT_PROCESSING.MULTIBANCO.COPIED' : 'COMMON.COPY'
    );
    return `${copyLabel} ${fieldLabel}`;
  }

  async copy(key: string, value: string | undefined): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.copiedKey.set(key);
      setTimeout(() => {
        if (this.copiedKey() === key) {
          this.copiedKey.set(null);
        }
      }, 2000);
    } catch {
      // Clipboard write can fail in non-secure contexts — silent no-op
    }
  }
}
